// v2 jeweller — a lapidary atelier: a cream timber-framed studio under a steep
// plum gable roof with a glazed skylight cut into the front slope, a treadle
// faceting wheel out front (flat grinding lap, spindle, drive flywheel and a gem
// arm resting a stone against the wheel), a glass-topped gem display case on dark
// velvet at the other corner, a hanging cut-gem sign over the door, and a crate
// of rough stones waiting by the east wall. Ported 1:1 from age-of-mcp's
// v2/jeweller.ts, using the library's shared `foundationSlab`, `scaffold` and
// `industry` (gableRoof) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const PLASTER = "#e6dcc4";  // studio walls
const TIMBER  = "#5a4632";  // framing posts / lintel / arm
const RIDGE   = "#4a3826";  // ridge beam
const ROOF    = "#6a4a78";  // plum roof — a violet echo of the trade
const GLASS   = "#bcd8e0";  // skylight, shop window, case cover
const DOOR    = "#46321e";
const BENCH   = "#7a5a3a";  // wheel-bench boards / stone crate
const WHEEL   = "#8a8d92";  // the stone grinding lap
const IRON    = "#4a4a4e";  // spindle / flywheel
const VELVET  = "#3a2a4a";  // display-case bed
const GEM_A   = "#5ad0e0";  // cyan gem
const GEM_B   = "#e05ad0";  // magenta gem
const GEM_C   = "#9a5ae0";  // violet gem

const BASE = 0.14;                   // foundation slab top
const BX = -0.04, BZ = -0.12;        // studio centre
const BW = 0.58, BD = 0.52;          // studio body size
const FRONT = BZ + BD / 2;           // +z face — 0.14
const EAVE = BASE + 0.50;            // wall top — 0.64
const WHEEL_X = 0.28, WHEEL_Z = 0.31;   // the faceting wheel out front

// The studio body, `h` of wall on the foundation.
function body(h: number): Part {
  return { geometry: new THREE.BoxGeometry(BW, h, BD), color: PLASTER,
    position: [BX, BASE + h / 2, BZ] };
}

// Dark timber corner posts framing the plaster, `h` high.
function framing(h: number): Part[] {
  const parts: Part[] = [];
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    parts.push({ geometry: new THREE.BoxGeometry(0.055, h, 0.055), color: TIMBER,
      position: [BX + x * (BW / 2), BASE + h / 2, BZ + z * (BD / 2)] });
  }
  return parts;
}

// The plum gable roof with a glazed skylight set into the front slope.
function roofAndSkylight(): Part[] {
  const ang = Math.atan2(0.30, 0.31);   // the front slope's pitch
  return [
    ...industry.gableRoof(BX, BZ, EAVE, 0.70, 0.62, 0.30, ROOF, RIDGE),
    { geometry: new THREE.BoxGeometry(0.20, 0.035, 0.18), color: TIMBER,
      position: [BX + 0.14, 0.775, 0.05], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.035, 0.13), color: GLASS,
      position: [BX + 0.14, 0.795, 0.05], rotation: [ang, 0, 0] },
  ];
}

// The shopfront: lintel band, door, glazed window on a sill, and the
// bracketed cut-gem sign hanging over the door.
function shopfront(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(BW, 0.05, 0.05), color: TIMBER,
      position: [BX, EAVE - 0.05, FRONT + 0.01] },
    { geometry: new THREE.BoxGeometry(0.17, 0.32, 0.04), color: DOOR,
      position: [-0.20, BASE + 0.16, FRONT + 0.02] },
    { geometry: new THREE.BoxGeometry(0.17, 0.15, 0.03), color: GLASS,
      position: [0.12, BASE + 0.28, FRONT + 0.015] },
    { geometry: new THREE.BoxGeometry(0.21, 0.03, 0.05), color: TIMBER,
      position: [0.12, BASE + 0.195, FRONT + 0.02] },
    { geometry: new THREE.BoxGeometry(0.03, 0.03, 0.16), color: TIMBER,
      position: [-0.20, 0.575, FRONT + 0.07] },
    { geometry: new THREE.OctahedronGeometry(0.055), color: GEM_B,
      position: [-0.20, 0.50, FRONT + 0.12] },
  ];
}

// The treadle faceting wheel: bench on trestles, flat grinding lap on its
// spindle, drive flywheel + treadle below, and the gem arm holding a stone
// against the lap.
function facetingWheel(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.26, 0.04, 0.22), color: BENCH,
      position: [WHEEL_X, BASE + 0.22, WHEEL_Z] },
    { geometry: new THREE.BoxGeometry(0.04, 0.20, 0.18), color: TIMBER,
      position: [WHEEL_X - 0.10, BASE + 0.10, WHEEL_Z] },
    { geometry: new THREE.BoxGeometry(0.04, 0.20, 0.18), color: TIMBER,
      position: [WHEEL_X + 0.10, BASE + 0.10, WHEEL_Z] },
    // Lap and flywheel are named so the "active" animation can spin them.
    { geometry: new THREE.CylinderGeometry(0.085, 0.085, 0.03, 8), color: WHEEL,
      position: [WHEEL_X, BASE + 0.265, WHEEL_Z], name: "lap" },
    { geometry: new THREE.CylinderGeometry(0.015, 0.015, 0.07, 5), color: IRON,
      position: [WHEEL_X, BASE + 0.315, WHEEL_Z], name: "lap" },
    { geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.025, 8), color: IRON,
      position: [WHEEL_X, BASE + 0.12, WHEEL_Z + 0.03], rotation: [0, 0, Math.PI / 2], name: "flywheel" },
    { geometry: new THREE.BoxGeometry(0.14, 0.02, 0.08), color: TIMBER,
      position: [WHEEL_X, BASE + 0.035, WHEEL_Z + 0.09], rotation: [0.35, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.025, 0.10, 0.025), color: TIMBER,
      position: [WHEEL_X + 0.08, BASE + 0.29, WHEEL_Z - 0.07] },
    { geometry: new THREE.BoxGeometry(0.16, 0.022, 0.022), color: TIMBER,
      position: [WHEEL_X + 0.035, BASE + 0.325, WHEEL_Z - 0.035],
      rotation: [0, 0.6, -0.12] },
    { geometry: new THREE.OctahedronGeometry(0.032), color: GEM_A,
      position: [WHEEL_X - 0.01, BASE + 0.305, WHEEL_Z] },
  ];
}

// The glass-topped display case: timber pedestal, velvet bed, three faceted
// stones under a glass cover.
function displayCase(): Part[] {
  const cx = -0.33, cz = 0.30;
  return [
    { geometry: new THREE.BoxGeometry(0.17, 0.20, 0.17), color: TIMBER,
      position: [cx, BASE + 0.10, cz] },
    { geometry: new THREE.BoxGeometry(0.15, 0.025, 0.15), color: VELVET,
      position: [cx, BASE + 0.212, cz] },
    { geometry: new THREE.OctahedronGeometry(0.028), color: GEM_A,
      position: [cx - 0.04, BASE + 0.25, cz - 0.03] },
    { geometry: new THREE.OctahedronGeometry(0.028), color: GEM_B,
      position: [cx + 0.04, BASE + 0.25, cz - 0.03] },
    { geometry: new THREE.OctahedronGeometry(0.028), color: GEM_C,
      position: [cx, BASE + 0.25, cz + 0.04] },
    { geometry: new THREE.BoxGeometry(0.15, 0.10, 0.15), color: GLASS,
      position: [cx, BASE + 0.275, cz] },
  ];
}

// A crate of rough, uncut stone waiting by the east wall.
function roughStones(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.15, 0.10, 0.15), color: BENCH,
      position: [0.30, BASE + 0.05, -0.30], rotation: [0, 0.2, 0] },
    { geometry: new THREE.OctahedronGeometry(0.035), color: "#9a948a",
      position: [0.30, BASE + 0.12, -0.30], rotation: [0.4, 0.7, 0] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), body(0.24), ...scaffold(0.82, 0.6)];
  if (s === 2) {
    return [foundationSlab(0.84), body(0.50), ...framing(0.50), ...scaffold(0.82, 1.0)];
  }
  return [
    foundationSlab(0.84),
    body(0.50),
    ...framing(0.50),
    ...roofAndSkylight(),
    ...shopfront(),
    ...facetingWheel(),
    ...displayCase(),
    ...roughStones(),
  ];
}

export const jewellerV2 = defineModel({
  id: "jeweller-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Jeweller (v2)",
  tags: ["building","jeweller","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly jeweller.",
  description: "A gem-cutter's bench that cuts raw crystals into faceted jewels.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The treadle wheel at work: the flat grinding lap spins on its spindle
      // while the drive flywheel below turns with it.
      id: "active",
      label: "Wheel turning",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        spin("lap", [WHEEL_X, 0.14 + 0.265, WHEEL_Z], { axis: "y", turns: 3 }),
        spin("flywheel", [WHEEL_X, 0.14 + 0.12, WHEEL_Z + 0.03], { axis: "x", turns: 3 }),
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0.28, 0.14, 0.51], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
